import React, { useState, useEffect, useRef, useCallback } from 'react';
import ModuleTemplate from '../../ModuleTemplate';
import { useInstructionAudio } from '../../lib/useInstructionAudio';
import './RhythmTest.css';

/* eslint-disable react-hooks/exhaustive-deps */

// Отладка: открыть тест с ?rhythmDebug=1 в адресе — внизу появится панель с временами хлопков.
// Данные также всегда пишутся в консоль браузера ([RhythmTest]).
const DEBUG = typeof window !== 'undefined' && /rhythmDebug/.test(window.location.search);

// ───────────── НАСТРОЙКИ ДЕТЕКЦИИ ХЛОПКОВ ─────────────
// Амплитуды — в шкале float -1..1 (не 0..128, как у byte-данных).
const GET_READY_MS = 800; // сколько горит «Готов?». Запись хлопков идёт с самого начала этой паузы, а не после неё.

const DETECT = {
  MIN_PEAK: 0.06,            // абсолютный минимум пика для хлопка
  PEAK_RATIO: 4,             // порог = max(MIN_PEAK, шум_пола * PEAK_RATIO)
  RISE_RATIO: 1.5,           // пик должен вырасти минимум в 1.5 раза относительно прошлого кадра (это "удар", а не гул)
  REFRACTORY_MS: 150,        // «мёртвое время» после хлопка (эхо/хвост не считается новым хлопком)
  HIGHPASS_HZ: 500,          // срезаем гул/низкие частоты, хлопок — широкополосный
  SILENCE_TIMEOUT_MS: 1800,  // тишина после последнего хлопка → подводим итог
  NO_CLAP_TIMEOUT_MS: 10000, // если вообще не хлопал → засчитываем как ошибку
  SETTLE_MIN_MS: 600,        // набрали нужное число хлопков → ждём ещё немного: не будет ли лишних (шум/эхо)
  MAX_EXTRA_CLAPS: 2,        // если лишних хлопков уже слишком много — сразу подводим итог
};

// ───────────── ДЛИННЫЙ / КОРОТКИЙ ─────────────
// «Длинный» и «короткий» — это ПАУЗА до следующего хлопка (звук хлопка один и тот же).
// Раньше времена в rhythmsData противоречили type: например, «два коротких» (4_1) хлопали через 600 мс,
// а «два длинных» (4_2) — тоже через 600 мс; «короткий» на 1000 мс и «длинный» на 1500 мс шли с одинаковой
// паузой 500 мс. Поэтому теперь времена строятся ИЗ type: пауза после бита = short или long.
// Контраст строго 1:2. [короткая, длинная] пауза в мс для каждой возрастной группы:
const SHORT_LONG_MS = {
  '4': [500, 1000],
  '5': [450, 900],
  '6': [400, 800],
  '7-9': [350, 700],
  '10-12': [300, 600],
  '13-15': [300, 600],
  '16+': [300, 600],
};

const buildRhythm = (r, shortMs, longMs) => {
  let t = 0;
  const events = r.events.map((e) => {
    const ev = { time: t, type: e.type };
    t += e.type === 'long' ? longMs : shortMs;
    return ev;
  });
  return { ...r, events, totalBeats: events.length, shortMs, longMs };
};

// ───────────── НАСТРОЙКИ ОЦЕНИВАНИЯ ─────────────
// Каждая пауза ребёнка классифицируется как «короткая» или «длинная» и сравнивается с эталоном.
// Точные миллисекунды не важны: важно, что короткое короткое, а длинное длинное.
const SCORING = {
  ALLOW_TEMPO_DRIFT: true,   // общий темп ребёнка может отличаться от эталона
  SCALE_MIN: 0.6,
  SCALE_MAX: 1.6,
  MAX_BAD_INTERVALS: 0,      // сколько неверных пауз допускается (0 = все должны совпасть)
};

const RhythmTest = ({ ageGroup, onBack, onComplete }) => {
  const { play: playInstruction, stop: stopInstruction } = useInstructionAudio();
  
  const rhythmsData = {
    '4': [
      { id: '4_1', events: [{ time: 0, type: 'short' }, { time: 600, type: 'short' }], totalBeats: 2, label: 'Два коротких' },
      { id: '4_2', events: [{ time: 0, type: 'long' }, { time: 600, type: 'long' }, { time: 1200, type: 'short' }, { time: 1600, type: 'short' }], totalBeats: 4, label: 'Два длинных + два коротких' },
      { id: '4_3', events: [{ time: 0, type: 'long' }, { time: 700, type: 'short' }, { time: 1000, type: 'short' }, { time: 1500, type: 'long' }], totalBeats: 4, label: 'Длинный + два коротких + длинный' },
    ],
    '5': [
      { id: '5_1', events: [{ time: 0, type: 'long' }, { time: 600, type: 'long' }, { time: 1200, type: 'short' }, { time: 1500, type: 'short' }, { time: 2000, type: 'long' }], totalBeats: 5, label: 'Два длинных + два коротких + длинный' },
      { id: '5_2', events: [{ time: 0, type: 'long' }, { time: 700, type: 'short' }, { time: 1000, type: 'short' }, { time: 1500, type: 'long' }, { time: 2000, type: 'long' }], totalBeats: 5, label: 'Один длинный + два коротких + два длинных' },
      { id: '5_3', events: [{ time: 0, type: 'short' }, { time: 500, type: 'short' }, { time: 1000, type: 'long' }, { time: 1500, type: 'long' }, { time: 2000, type: 'long' }], totalBeats: 5, label: 'Два коротких + три длинных' },
    ],
    '6': [
      { id: '6_1', events: [{ time: 0, type: 'long' }, { time: 600, type: 'long' }, { time: 1200, type: 'short' }, { time: 1500, type: 'short' }, { time: 2000, type: 'long' }, { time: 2500, type: 'long' }], totalBeats: 6, label: 'Два длинных + два коротких + два длинных' },
      { id: '6_2', events: [{ time: 0, type: 'short' }, { time: 500, type: 'short' }, { time: 1000, type: 'long' }, { time: 1500, type: 'long' }, { time: 2000, type: 'long' }, { time: 2500, type: 'long' }], totalBeats: 6, label: 'Два коротких + четыре длинных' },
      { id: '6_3', events: [{ time: 0, type: 'long' }, { time: 700, type: 'short' }, { time: 1000, type: 'short' }, { time: 1400, type: 'short' }], totalBeats: 4, label: 'Один длинный + три коротких' },
    ],
    '7-9': [
      { id: '7_1', events: [{ time: 0, type: 'long' }, { time: 500, type: 'short' }, { time: 900, type: 'long' }, { time: 1400, type: 'short' }, { time: 1800, type: 'long' }, { time: 2300, type: 'short' }], totalBeats: 6, label: 'Сложный ритм 1' },
      { id: '7_2', events: [{ time: 0, type: 'short' }, { time: 400, type: 'long' }, { time: 800, type: 'short' }, { time: 1200, type: 'long' }, { time: 1600, type: 'short' }, { time: 2000, type: 'long' }, { time: 2400, type: 'short' }], totalBeats: 7, label: 'Сложный ритм 2' },
      { id: '7_3', events: [{ time: 0, type: 'long' }, { time: 600, type: 'long' }, { time: 900, type: 'short' }, { time: 1300, type: 'long' }, { time: 1700, type: 'short' }, { time: 2000, type: 'long' }, { time: 2400, type: 'long' }, { time: 2800, type: 'short' }], totalBeats: 8, label: 'Сложный ритм 3' },
    ],
    '10-12': [
      { id: '10_1', events: [{ time: 0, type: 'long' }, { time: 400, type: 'short' }, { time: 700, type: 'long' }, { time: 1100, type: 'short' }, { time: 1500, type: 'long' }, { time: 1900, type: 'short' }, { time: 2300, type: 'long' }], totalBeats: 7, label: 'Сложный ритм 1' },
      { id: '10_2', events: [{ time: 0, type: 'short' }, { time: 300, type: 'long' }, { time: 700, type: 'short' }, { time: 1000, type: 'long' }, { time: 1400, type: 'short' }, { time: 1800, type: 'long' }, { time: 2200, type: 'short' }, { time: 2600, type: 'long' }], totalBeats: 8, label: 'Сложный ритм 2' },
      { id: '10_3', events: [{ time: 0, type: 'long' }, { time: 500, type: 'long' }, { time: 800, type: 'short' }, { time: 1200, type: 'long' }, { time: 1600, type: 'short' }, { time: 1900, type: 'long' }, { time: 2300, type: 'long' }, { time: 2700, type: 'short' }, { time: 3100, type: 'long' }], totalBeats: 9, label: 'Сложный ритм 3' },
    ],
    '13-15': [
      { id: '13_1', events: [{ time: 0, type: 'long' }, { time: 400, type: 'short' }, { time: 700, type: 'long' }, { time: 1000, type: 'short' }, { time: 1400, type: 'long' }, { time: 1800, type: 'short' }, { time: 2200, type: 'long' }, { time: 2600, type: 'short' }], totalBeats: 8, label: 'Сложный ритм 1' },
      { id: '13_2', events: [{ time: 0, type: 'short' }, { time: 300, type: 'long' }, { time: 600, type: 'short' }, { time: 900, type: 'long' }, { time: 1300, type: 'short' }, { time: 1700, type: 'long' }, { time: 2100, type: 'short' }, { time: 2500, type: 'long' }, { time: 2900, type: 'short' }], totalBeats: 9, label: 'Сложный ритм 2' },
      { id: '13_3', events: [{ time: 0, type: 'long' }, { time: 400, type: 'long' }, { time: 700, type: 'short' }, { time: 1000, type: 'long' }, { time: 1400, type: 'short' }, { time: 1700, type: 'long' }, { time: 2100, type: 'long' }, { time: 2500, type: 'short' }, { time: 2900, type: 'long' }, { time: 3300, type: 'short' }], totalBeats: 10, label: 'Сложный ритм 3' },
    ],
    '16+': [
      { id: '16_1', events: [{ time: 0, type: 'long' }, { time: 400, type: 'short' }, { time: 800, type: 'long' }, { time: 1200, type: 'short' }, { time: 1600, type: 'long' }, { time: 2000, type: 'short' }, { time: 2400, type: 'long' }, { time: 2800, type: 'short' }, { time: 3200, type: 'long' }], totalBeats: 9, label: 'Сложный ритм 1' },
      { id: '16_2', events: [{ time: 0, type: 'short' }, { time: 300, type: 'long' }, { time: 600, type: 'short' }, { time: 900, type: 'long' }, { time: 1300, type: 'short' }, { time: 1700, type: 'long' }, { time: 2100, type: 'short' }, { time: 2500, type: 'long' }, { time: 2900, type: 'short' }, { time: 3300, type: 'long' }], totalBeats: 10, label: 'Сложный ритм 2' },
      { id: '16_3', events: [{ time: 0, type: 'long' }, { time: 400, type: 'long' }, { time: 700, type: 'short' }, { time: 1000, type: 'long' }, { time: 1400, type: 'short' }, { time: 1700, type: 'long' }, { time: 2100, type: 'long' }, { time: 2500, type: 'short' }, { time: 2900, type: 'long' }, { time: 3300, type: 'short' }, { time: 3700, type: 'long' }], totalBeats: 11, label: 'Сложный ритм 3' },
    ],
  };

  const [currentRhythmIndex, setCurrentRhythmIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [playedBeats, setPlayedBeats] = useState([]);
  const [userClaps, setUserClaps] = useState([]);
  const [beatResults, setBeatResults] = useState([]); // итог по каждому биту: true/false (после оценки)
  const [showResult, setShowResult] = useState(false);
  const [rhythmResult, setRhythmResult] = useState(null);
  const [phase, setPhase] = useState('loading');
  const [isClapping, setIsClapping] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [rhythmLog, setRhythmLog] = useState([]);
  const [buffersReady, setBuffersReady] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);
  
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const phaseRef = useRef('loading');
  const clapBufferRef = useRef(null);
  const startTimeRef = useRef(0);
  const animationRef = useRef(null);

  // --- детектор хлопков ---
  const analyserRef = useRef(null);
  const dataRef = useRef(null);
  const micPromiseRef = useRef(null);
  const capturingRef = useRef(false);      // true только в фазе 'clap', пока принимаем хлопки
  const clapsRef = useRef([]);             // абсолютные времена хлопков (performance.now)
  const floorRef = useRef(0.01);           // адаптивный уровень фонового шума (по пику)
  const lastPeakRef = useRef(0);
  const lastClapTsRef = useRef(0);
  const clapPeaksRef = useRef([]);         // пик каждого принятого хлопка (для отладки)
  const rejectedRef = useRef([]);          // «почти хлопки», отброшенные детектором (для отладки)
  const lastRejectTsRef = useRef(0);
  const silenceTimerRef = useRef(null);
  const timersRef = useRef([]);
  const currentRhythmRef = useRef(null);
  const evaluateRef = useRef(() => {});

  // Ниже в rhythmsData поле time не используется: времена строятся из type (см. SHORT_LONG_MS)
  const groupKey = rhythmsData[ageGroup] ? ageGroup : '6';
  const [shortMs, longMs] = SHORT_LONG_MS[groupKey];
  const rhythms = rhythmsData[groupKey].map(r => buildRhythm(r, shortMs, longMs));
  const currentRhythm = rhythms[currentRhythmIndex];
  currentRhythmRef.current = currentRhythm;
  const totalRhythms = rhythms.length;
  const isLastRhythm = currentRhythmIndex === totalRhythms - 1;

  const schedule = (fn, ms) => {
    const id = setTimeout(fn, ms);
    timersRef.current.push(id);
    return id;
  };

  const stopDetectorLoop = () => {
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
    animationRef.current = null;
  };

  useEffect(() => {
    return () => {
      capturingRef.current = false;
      stopDetectorLoop();
      clearTimeout(silenceTimerRef.current);
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
      if (streamRef.current) streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
      analyserRef.current = null;
      micPromiseRef.current = null;
      const ctx = audioContextRef.current;
      if (ctx && ctx.state !== 'closed') ctx.close();
      audioContextRef.current = null;
    };
  }, []);

  // Генерация только ОДНОГО короткого звука хлопка
  const generateClapBuffer = (context) => {
    const duration = 0.12;
    const sampleRate = context.sampleRate;
    const samples = Math.floor(sampleRate * duration);
    const buffer = context.createBuffer(1, samples, sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < samples; i++) {
      const t = i / sampleRate;
      const attack = 1 - Math.exp(-t * 80);
      const decay = Math.exp(-t * 12);
      const envelope = attack * decay * 0.9;
      const noise = (Math.random() * 2 - 1) * 0.7;
      const click = Math.sin(t * 4000) * Math.exp(-t * 100) * 0.3;
      const resonance = Math.sin(t * 800) * Math.exp(-t * 15) * 0.4;
      data[i] = (noise + click + resonance) * envelope;
    }
    return buffer;
  };

  useEffect(() => {
    let context = audioContextRef.current;
    if (!context || context.state === 'closed') {
      context = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = context;
    }
    clapBufferRef.current = generateClapBuffer(context);
    setBuffersReady(true);
    phaseRef.current = 'listen';
    setPhase('listen'); 
  }, []);

  // offsetSec — смещение от «сейчас» по часам AudioContext (точное планирование, без дрожания setTimeout)
  const playClapSound = (offsetSec = 0) => {
    let context = audioContextRef.current;
    if (!context || context.state === 'closed') {
      context = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = context;
      clapBufferRef.current = generateClapBuffer(context);
    }
    if (context.state === 'suspended') context.resume();
    
    const buffer = clapBufferRef.current;
    if (!buffer) return;
    
    const source = context.createBufferSource();
    source.buffer = buffer;
    const gainNode = context.createGain();
    gainNode.gain.value = 0.8;
    source.connect(gainNode);
    gainNode.connect(context.destination);
    source.start(context.currentTime + offsetSec);
  };

  const animateClap = () => {
    setIsClapping(true);
    setTimeout(() => setIsClapping(false), 150);
  };

  // ───────────── МИКРОФОН И ДЕТЕКТОР ─────────────

  // Микрофон открывается ОДИН раз на весь тест (без повторных запросов разрешения),
  // в том же AudioContext, что и воспроизведение, и БЕЗ эхоподавления / шумоподавления / АРУ:
  // эти фильтры браузера воспринимают хлопок как «шум» и гасят его.
  const ensureMic = () => {
    if (analyserRef.current) return Promise.resolve(true);
    if (!micPromiseRef.current) {
      micPromiseRef.current = (async () => {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
          });
          streamRef.current = stream;
          const ctx = audioContextRef.current;
          if (ctx.state === 'suspended') await ctx.resume();
          const src = ctx.createMediaStreamSource(stream);
          const hp = ctx.createBiquadFilter();
          hp.type = 'highpass';
          hp.frequency.value = DETECT.HIGHPASS_HZ;
          const analyser = ctx.createAnalyser();
          analyser.fftSize = 1024;            // ~21 мс при 48 кГц: окно > шага кадра (16.7 мс), «дыр» между кадрами нет
          analyser.smoothingTimeConstant = 0;
          src.connect(hp);
          hp.connect(analyser);
          analyserRef.current = analyser;
          dataRef.current = new Float32Array(analyser.fftSize);
          return true;
        } catch (err) {
          console.error('Ошибка доступа к микрофону:', err);
          micPromiseRef.current = null;
          return false;
        }
      })();
    }
    return micPromiseRef.current;
  };

  const registerClap = (ts, peak) => {
    lastClapTsRef.current = ts;
    clapsRef.current.push(ts);
    clapPeaksRef.current.push(Number(peak.toFixed(3)));
    animateClap();
    setUserClaps([...clapsRef.current]);
    setPlayedBeats(prev => [...prev, prev.length]);

    clearTimeout(silenceTimerRef.current);
    const events = currentRhythmRef.current.events;
    const n = events.length;
    const count = clapsRef.current.length;

    if (count >= n + DETECT.MAX_EXTRA_CLAPS) {
      evaluateRef.current();
      return;
    }
    // Набрали n хлопков — не завершаем мгновенно: если среди них был ложный (шум/эхо),
    // настоящий последний хлопок ещё придёт. Ждём чуть дольше самой длинной паузы ритма.
    let wait = DETECT.SILENCE_TIMEOUT_MS;
    if (count >= n) {
      let maxGap = 0;
      for (let i = 1; i < n; i++) maxGap = Math.max(maxGap, events[i].time - events[i - 1].time);
      wait = Math.max(DETECT.SETTLE_MIN_MS, maxGap * 1.2);
    }
    silenceTimerRef.current = setTimeout(() => evaluateRef.current(), wait);
  };

  const startDetectorLoop = () => {
    if (animationRef.current || !analyserRef.current) return;
    const loop = () => {
      // следующий кадр планируем СРАЗУ: если evaluate() остановит цикл, он отменит именно его
      animationRef.current = requestAnimationFrame(loop);

      const analyser = analyserRef.current;
      if (!analyser) return;
      const data = dataRef.current;
      analyser.getFloatTimeDomainData(data);

      let peak = 0;
      for (let i = 0; i < data.length; i++) {
        const a = Math.abs(data[i]);
        if (a > peak) peak = a;
      }

      const now = performance.now();
      const threshold = Math.max(DETECT.MIN_PEAK, floorRef.current * DETECT.PEAK_RATIO);

      if (capturingRef.current) {
        const isLoud = peak > threshold;
        const isRising = peak > lastPeakRef.current * DETECT.RISE_RATIO;
        const isFree = now - lastClapTsRef.current > DETECT.REFRACTORY_MS;
        if (isLoud && isRising && isFree) {
          registerClap(now, peak);
        } else if (peak > threshold * 0.6 && now - lastRejectTsRef.current > 100 && rejectedRef.current.length < 30) {
          // для отладки: что было похоже на хлопок, но не принято и почему
          lastRejectTsRef.current = now;
          rejectedRef.current.push({
            t: Math.round(now - startTimeRef.current),
            peak: Number(peak.toFixed(3)),
            thr: Number(threshold.toFixed(3)),
            why: !isLoud ? 'ниже порога' : !isFree ? 'мёртвое время' : 'нет резкого роста',
          });
        }
      }

      // фон обновляем только по «тихим» кадрам (сам хлопок в фон не попадает)
      if (peak < threshold) floorRef.current = floorRef.current * 0.95 + peak * 0.05;
      lastPeakRef.current = peak;
    };
    animationRef.current = requestAnimationFrame(loop);
  };

  // «Разогрев» микрофона в фазе get_ready: разрешение, калибровка шума — до начала записи
  const prepareMic = async () => {
    const ok = await ensureMic();
    if (ok) startDetectorLoop();
  };

  const cancelCapture = () => {
    capturingRef.current = false;
    clearTimeout(silenceTimerRef.current);
    stopDetectorLoop();
    setIsListening(false);
  };

  const runRhythmLogic = () => {
    if (isPlaying) return;
    setIsPlaying(true);
    phaseRef.current = 'listen';
    setPhase('listen');
    const events = currentRhythm.events;
    const totalDuration = events[events.length - 1].time + 500;
    setPlayedBeats([]);
    setBeatResults([]);
    prepareMic(); // открываем микрофон заранее (во время прослушивания), чтобы запись стартовала мгновенно
    
    // Воспроизводим ритм: звук планируется по часам AudioContext (точно), анимация — по таймеру
    events.forEach((event, index) => {
      playClapSound(0.05 + event.time / 1000);
      schedule(() => {
        animateClap();
        setPlayedBeats(prev => [...prev, index]);
      }, 50 + event.time);
    });

    // После окончания ритма даем пациенту 1.5с на подготовку
    schedule(() => {
      setIsPlaying(false);
      phaseRef.current = 'get_ready';
      setPhase('get_ready');
      setPlayedBeats([]);
      setUserClaps([]);
      setBeatResults([]);
      // ЗАПИСЬ НАЧИНАЕТСЯ СРАЗУ, а не после «Готов?»: ребёнок часто начинает повторять ритм
      // сразу после прослушивания, и первый хлопок терялся (в фазе get_ready микрофон не слушал).
      setIsListening(true);

      schedule(() => {
        if (phaseRef.current === 'get_ready') {
          phaseRef.current = 'clap';
          setPhase('clap');
        }
      }, GET_READY_MS);
    }, 50 + totalDuration);
  };

  useEffect(() => {
    if (!buffersReady || !currentRhythm) return;
    if (phaseRef.current === 'clap' || phaseRef.current === 'result' || phaseRef.current === 'get_ready') return;

    const timer = setTimeout(() => {
      const proceed = () => {
        if (currentRhythmIndex === 0) {
          let fallbackTriggered = false;
          const playClaps = () => {
            if (fallbackTriggered) return;
            fallbackTriggered = true;
            runRhythmLogic();
          };
          playInstruction("Послушай ритм и повтори", playClaps);
          setTimeout(() => { if (!fallbackTriggered) playClaps(); }, 2500);
        } else {
          runRhythmLogic();
        }
      };

      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume().then(proceed);
      } else {
        proceed();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [currentRhythmIndex, buffersReady]);

  const playRhythm = () => {
    if (isPlaying || !clapBufferRef.current) return;
    stopInstruction();
    cancelCapture(); // если нажали «Послушай» во время записи — микрофон не должен слышать динамик
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume().then(() => runRhythmLogic());
    } else {
      runRhythmLogic();
    }
  };

  // ───────────── ОЦЕНИВАНИЕ ─────────────
  // 1) паузы между соседними хлопками; 2) подгоняем общий темп; 3) каждая пауза → «short» / «long»
  // (граница — среднее геометрическое short и long, т.е. ×1.41 от короткой); 4) сравниваем с эталоном.
  // Если хлопков больше, чем нужно (шум/эхо), перебираем все «окна» из n подряд идущих и берём лучшее.
  const scoreWindow = (ts, expI, expClass, thr) => {
    const uI = [];
    for (let i = 1; i < ts.length; i++) uI.push(ts[i] - ts[i - 1]);

    let scale = 1;
    if (SCORING.ALLOW_TEMPO_DRIFT && uI.length > 0) {
      const sumE = expI.slice(0, uI.length).reduce((a, b) => a + b, 0);
      const sumU = uI.reduce((a, b) => a + b, 0);
      if (sumE > 0 && sumU > 0) scale = Math.min(SCORING.SCALE_MAX, Math.max(SCORING.SCALE_MIN, sumU / sumE));
    }

    let dev = 0;
    const userClass = [];
    const intervalOk = expClass.map((c, i) => {
      if (i >= uI.length) return false;
      const u = uI[i] / scale;
      dev += Math.abs(Math.log(u / expI[i]));
      const cls = u < thr ? 'short' : 'long';
      userClass.push(cls);
      return cls === c;
    });
    const bad = intervalOk.filter(ok => !ok).length;
    return { intervalOk, userClass, bad, dev, scale, uI };
  };

  const checkRhythm = (claps) => {
    const rhythm = currentRhythmRef.current;
    const events = rhythm.events;
    const n = events.length;
    const thr = Math.sqrt(rhythm.shortMs * rhythm.longMs);
    const expI = [];
    for (let i = 1; i < n; i++) expI.push(events[i].time - events[i - 1].time);
    // тип ПОСЛЕДНЕГО бита проверить нельзя: после него нет следующего хлопка
    const expClass = events.slice(0, n - 1).map(e => e.type);

    let best = null;
    const maxOffset = Math.max(0, claps.length - n);
    for (let off = 0; off <= maxOffset; off++) {
      const w = scoreWindow(claps.slice(off, off + n), expI, expClass, thr);
      if (!best || w.bad < best.bad || (w.bad === best.bad && w.dev < best.dev)) best = { ...w, off };
    }

    // бит i (i>=1) верен, если верна пауза, которая к нему привела; первый бит — «якорь»
    const perBeat = [claps.length > 0, ...best.intervalOk];
    const correctCount = perBeat.filter(Boolean).length;
    const isSuccess = claps.length >= n && best.bad <= SCORING.MAX_BAD_INTERVALS;
    return { isSuccess, correctCount, perBeat, best, expI, expClass, thr };
  };

  const finishRhythm = (isSuccess) => {
    setIsListening(false);
    setShowResult(true);
    setRhythmResult(isSuccess);
    phaseRef.current = 'result';
    setPhase('result');
    
    const newRhythmLog = [...rhythmLog, { id: currentRhythm.id, label: currentRhythm.label, totalBeats: currentRhythm.totalBeats, success: isSuccess }];
    setRhythmLog(newRhythmLog);
    setAnswers(prev => [...prev, isSuccess]); 

    if (isLastRhythm) {
      setTimeout(() => {
        const correctCount = answers.filter(a => a === true).length + (isSuccess ? 1 : 0);
        const score = totalRhythms > 0 ? Math.round((correctCount / totalRhythms) * 100) : 0;
        const failedRhythms = newRhythmLog.filter(r => !r.success).map(r => ({ label: r.label }));
        if (onComplete) {
          onComplete({ testId: 'RhythmTest', score, details: { total: totalRhythms, correct: correctCount, failedRhythms } });
        } else {
          onBack();
        }
      }, 1500);
    } else {
      setTimeout(handleNext, 1500); 
    }
  };

  // Единая точка завершения записи: набрали хлопки и подождали лишние / тишина / таймаут
  const evaluate = () => {
    if (!capturingRef.current) return;
    capturingRef.current = false;
    clearTimeout(silenceTimerRef.current);
    stopDetectorLoop();
    const claps = clapsRef.current;
    const { isSuccess, perBeat, best, expI, expClass, thr } = checkRhythm(claps);

    const info = {
      rhythm: currentRhythmRef.current.id,
      success: isSuccess,
      claps_detected: claps.length,
      claps_expected: currentRhythmRef.current.events.length,
      window_offset: best.off,
      expected_intervals_ms: expI,
      expected_classes: expClass,
      user_intervals_ms: best.uI.map(x => Math.round(x)),
      user_intervals_scaled_ms: best.uI.map(x => Math.round(x / best.scale)),
      user_classes: best.userClass,
      short_long_border_ms: Math.round(thr),
      tempo_scale: Number(best.scale.toFixed(2)),
      interval_ok: best.intervalOk,
      clap_peaks: clapPeaksRef.current,
      noise_floor: Number(floorRef.current.toFixed(3)),
      rejected_candidates: rejectedRef.current,
    };
    console.log('[RhythmTest]', info);
    if (DEBUG) setDebugInfo(info);

    setBeatResults(perBeat);
    finishRhythm(isSuccess);
  };
  evaluateRef.current = evaluate; // цикл детектора живёт дольше одного рендера — всегда зовём свежую версию

  const startListening = async () => {
    const ok = await ensureMic();
    if (!ok) {
      alert('Пожалуйста, разрешите доступ к микрофону');
      setIsListening(false);
      return;
    }
    if (phaseRef.current !== 'clap' && phaseRef.current !== 'get_ready') return; // например, успели нажать «Послушай»

    clapsRef.current = [];
    lastClapTsRef.current = 0;
    lastPeakRef.current = 0;
    clapPeaksRef.current = [];
    rejectedRef.current = [];
    startTimeRef.current = performance.now();
    capturingRef.current = true;
    startDetectorLoop();

    clearTimeout(silenceTimerRef.current);
    silenceTimerRef.current = setTimeout(() => evaluateRef.current(), DETECT.NO_CLAP_TIMEOUT_MS);
  };

  useEffect(() => { if (isListening) startListening(); }, [isListening]);

  const handleNext = () => {
    if (currentRhythmIndex < totalRhythms - 1) {
      setCurrentRhythmIndex(prev => prev + 1);
      setShowResult(false);
      setRhythmResult(null);
      setPlayedBeats([]);
      setUserClaps([]);
      setBeatResults([]);
      setIsListening(false);
      setPhase('listen');
      phaseRef.current = 'listen';
    }
  };

  return (
    <ModuleTemplate 
      progress={answers} 
      totalQuestions={totalRhythms} 
      forceStop={true}
      onComplete={onComplete || onBack}
      extraContent="Послушай ритм и повтори"
    >
      <div className="rhythm-test-card">
        <div className="rhythm-area">
          <div className="rhythm-top-row">
            <button className={`rhythm-big-btn clap-btn ${phase === 'clap' ? 'active' : ''}`} disabled={phase !== 'clap'}>
              <span className="btn-icon">👏</span>
              <span className="btn-label">Похлопай</span>
            </button>

            <div className={`clap-hands ${isClapping ? 'clap' : ''}`}>👏</div>

            <button className={`rhythm-big-btn listen-btn ${phase === 'listen' ? 'active' : ''}`} onClick={playRhythm} disabled={isPlaying || phase === 'result' || phase === 'loading' || phase === 'get_ready'}>
              <span className="btn-icon">🔊</span>
              <span className="btn-label">Послушай</span>
            </button>
          </div>

          {phase === 'get_ready' && (
            <div className="status-clap">Внимание... Готов?</div>
          )}

           <div className="rhythm-progress">
            {Array.from({ length: currentRhythm.totalBeats }).map((_, idx) => {
              let dotClass = 'rhythm-dot';
              
              // Во время прослушивания и хлопков кружочки просто ярко-белые (показывают прогресс)
              if ((phase === 'listen' || phase === 'clap') && idx < playedBeats.length) {
                dotClass += ' lit';
              } 
              // В фазе результата красим только в красный или зеленый
              else if (phase === 'result' && beatResults[idx] !== undefined) {
                dotClass += beatResults[idx] ? ' active correct' : ' active wrong';
              }
              
              return <div key={idx} className={dotClass} />;
            })}
          </div>

          <div className="rhythm-status">
            {phase === 'clap' && <span className="status-clap">👏 Хлопай!</span>}
            {phase === 'result' && (
              <span className={`status-result ${rhythmResult ? 'success' : 'error'}`}>
                {rhythmResult ? '✅ Отлично!' : '🔄 Попробуй в следующий раз'}
              </span>
            )}
          </div>
        </div>
        {DEBUG && debugInfo && (
          <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, maxHeight: '50vh', overflow: 'auto', padding: 8, background: 'rgba(0,0,0,0.88)', color: '#9fef9f', zIndex: 9999 }}>
            <button
              style={{ marginBottom: 6, padding: '6px 12px', fontSize: 14 }}
              onClick={() => navigator.clipboard && navigator.clipboard.writeText(Object.entries(debugInfo).map(([k, v]) => k + ': ' + JSON.stringify(v)).join('\n'))}
            >
              Копировать
            </button>
            <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {Object.entries(debugInfo).map(([k, v]) => k + ': ' + JSON.stringify(v)).join('\n')}
            </pre>
          </div>
        )}
      </div>
    </ModuleTemplate>
  );
};

export default RhythmTest;